import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  message,
  Card,
  Row,
  Col,
  Tag,
  Popconfirm,
  Tooltip,
  Modal,
  Form,
  DatePicker,
} from "antd";
import moment from "moment";
import {
  SearchOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EditOutlined,
  WalletOutlined,
  FileTextOutlined,
  UserOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import apiService from "../../services/api";
import ErrorBoundary from "../common/ErrorBoundary";
import {
  formatVolume,
  formatExchangeName,
  formatTradeType,
  getTradeTypeColor,
  formatDateTime,
} from "../../utils/formatters";
import { useAuth } from "../../hooks/useAuth";
import "./AccountBalanceTable.css";

const { Option } = Select;
const { RangePicker } = DatePicker;

// 重算权益覆盖的天数：收益按 T+1 链式回填（T 日重算回填 T-1 日盈亏），
// 历史余额或资金流被修正后只重算当天无法修正近期收益，需整段重算最近 30 天
const EQUITY_RECALC_DAYS = 30;

// 空筛选条件（business_line_id/strategy_type_id：undefined 表示不筛选，0 表示未分配）
const EMPTY_FILTERS = {
  account: "",
  exchange: "",
  trade_type: "",
  asset: "",
  business_line_id: undefined,
  strategy_type_id: undefined,
  dateRange: null,
};

// 将筛选条件转换为请求参数（列表与统计共用）
const buildFilterParams = (filters) => {
  const params = { ...filters };
  if (params.dateRange && params.dateRange.length === 2) {
    params.date_from = params.dateRange[0].format("YYYY-MM-DD");
    params.date_to = params.dateRange[1].format("YYYY-MM-DD");
  }
  delete params.dateRange;

  // 移除空值参数（保留 0：业务线/策略类型的「未分配」）
  Object.keys(params).forEach((key) => {
    if (params[key] === "" || params[key] === undefined || params[key] === null) {
      delete params[key];
    }
  });
  return params;
};

// 金额格式化（千分位 + 两位小数）
const formatUSDT = (value) =>
  (value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const AccountBalanceTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total_records: 0,
    total_accounts: 0,
    total_exchanges: 0,
    total_assets: 0,
    latest_value_usdt: 0,
    latest_spot_value_usdt: 0,
    latest_swap_value_usdt: 0,
    latest_snapshot_date: "",
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addForm] = Form.useForm();

  const [activeAccounts, setActiveAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [exchanges, setExchanges] = useState([]);
  const [loadingExchanges, setLoadingExchanges] = useState(false);
  const [businessLines, setBusinessLines] = useState([]);
  const [strategyTypes, setStrategyTypes] = useState([]);
  const [loadingMasterData, setLoadingMasterData] = useState(false);

  // 时间快捷选项
  const timePresets = [
    { label: "1天", value: "1d" },
    { label: "3天", value: "3d" },
    { label: "7天", value: "7d" },
    { label: "近30天", value: "30d" },
    { label: "本月", value: "thisMonth" },
    { label: "上月", value: "lastMonth" },
    { label: "自定义日期", value: "custom" },
  ];

  const [timePreset, setTimePreset] = useState("");
  const [showCustomRange, setShowCustomRange] = useState(false);

  // 计算时间范围
  const getDateRangeFromPreset = (preset) => {
    const today = moment();
    const yesterday = today.clone().subtract(1, "days");
    switch (preset) {
      case "1d":
        return [yesterday.clone().startOf("day"), yesterday.clone().endOf("day")];
      case "3d":
        return [today.clone().subtract(3, "days").startOf("day"), yesterday.clone().endOf("day")];
      case "7d":
        return [today.clone().subtract(7, "days").startOf("day"), yesterday.clone().endOf("day")];
      case "30d":
        return [today.clone().subtract(30, "days").startOf("day"), yesterday.clone().endOf("day")];
      case "thisMonth":
        return [today.clone().startOf("month"), today.clone().endOf("month")];
      case "lastMonth":
        return [
          today.clone().subtract(1, "month").startOf("month"),
          today.clone().subtract(1, "month").endOf("month"),
        ];
      default:
        return null;
    }
  };

  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  // 获取数据（overrideFilters 用于重置等需要立即生效的场景）
  const fetchData = useCallback(
    async (params = {}, overrideFilters = null) => {
      setLoading(true);
      try {
        const { page, pageSize, ...rest } = params;
        const queryParams = {
          page: page || pagination.current,
          page_size: pageSize || pagination.pageSize,
          ...rest, // order_by / order_dir 等附加参数
          ...buildFilterParams(overrideFilters || filters),
        };

        const response = await apiService.balances.getBalances(queryParams);

        if (response.data?.success) {
          const result = response.data.data || {};
          setData(result.list || []);
          setSelectedRowKeys([]); // Reset selection on data fetch
          setPagination((prev) => ({
            ...prev,
            current: result.page || 1,
            pageSize: result.page_size || 20,
            total: result.total || 0,
          }));
        }
      } catch (error) {
        console.error("加载数据失败:", error);
        message.error("加载数据失败: " + (error.apiMessage || error.message));
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.current, pagination.pageSize],
  );

  // 获取统计数据（与列表使用相同筛选条件，卡片跟随筛选联动）
  const fetchStats = async (overrideFilters = null) => {
    try {
      const response = await apiService.balances.getStats(
        buildFilterParams(overrideFilters || filters),
      );
      if (response.data?.success) {
        setStats(response.data.data || {});
      }
    } catch (error) {
      console.error("获取统计失败:", error);
    }
  };

  // 获取账户、交易所与业务线/策略类型主数据
  const fetchFilterOptions = async () => {
    try {
      setLoadingAccounts(true);
      setLoadingExchanges(true);
      setLoadingMasterData(true);
      const [accountsRes, exchangesRes, blRes, stRes] = await Promise.all([
        apiService.trades.getActiveAccounts(),
        apiService.exchanges.getActiveExchanges(),
        apiService.businessLines.listActive(),
        apiService.strategyTypes.listActive(),
      ]);

      if (accountsRes.data?.success) {
        setActiveAccounts(accountsRes.data.data || []);
      }
      if (exchangesRes.data?.success) {
        setExchanges(exchangesRes.data.data || []);
      }
      if (blRes.data?.success) {
        setBusinessLines(blRes.data.data || []);
      }
      if (stRes.data?.success) {
        setStrategyTypes(stRes.data.data || []);
      }
    } catch (error) {
      console.error("加载过滤选项失败:", error);
    } finally {
      setLoadingAccounts(false);
      setLoadingExchanges(false);
      setLoadingMasterData(false);
    }
  };

  // 初始加载
  useEffect(() => {
    fetchData();
    fetchStats();
    fetchFilterOptions();
  }, []);

  // 重算权益：重新计算最近 30 天的收益链
  const handleRecalculate = async () => {
    try {
      setLoading(true);
      const today = moment().format("YYYY-MM-DD");
      const response = await apiService.equity.recalculate({
        date: today,
        days: EQUITY_RECALC_DAYS,
      });
      if (response.data?.success) {
        message.success(`已启动后台重算任务（最近 ${EQUITY_RECALC_DAYS} 天）`);
      }
    } catch (error) {
      message.error("触发重算失败: " + (error.apiMessage || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 删除记录
  const handleDelete = async (id) => {
    try {
      const response = await apiService.balances.deleteBalance(id);
      if (response.data?.success) {
        message.success("删除成功");
        fetchData();
        fetchStats();
      }
    } catch (error) {
      message.error("删除失败: " + (error.apiMessage || error.message));
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) return;

    try {
      const response = await apiService.balances.deleteBalancesBatch(selectedRowKeys);
      if (response.data?.success) {
        message.success(`批量删除 ${selectedRowKeys.length} 条记录成功`);
        setSelectedRowKeys([]);
        fetchData();
        fetchStats();
      }
    } catch (error) {
      console.error("批量删除失败:", error);
      message.error("批量删除失败: " + (error.apiMessage || error.message));
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setIsEditModalOpen(true);
    setTimeout(() => {
      form.setFieldsValue({
        ...record,
        snapshot_date: record.snapshot_date ? moment(record.snapshot_date) : null,
        created_at: record.created_at ? moment(record.created_at) : null,
        updated_at: record.updated_at ? moment(record.updated_at) : null,
      });
    }, 0);
  };

  const handleEditSubmit = async (values) => {
    try {
      const payload = { ...values };
      if (payload.snapshot_date) {
        payload.snapshot_date = payload.snapshot_date.format("YYYY-MM-DD");
      }
      if (payload.created_at) {
        payload.created_at = payload.created_at.toISOString();
      }
      if (payload.updated_at) {
        payload.updated_at = payload.updated_at.toISOString();
      }
      const response = await apiService.balances.updateBalance(editingRecord.id, payload);
      if (response.data?.success) {
        message.success("更新成功");
        setIsEditModalOpen(false);
        fetchData();
        fetchStats();
      }
    } catch (error) {
      message.error("更新失败: " + (error.apiMessage || error.message));
    }
  };

  // 打开新增弹窗
  const handleOpenAdd = () => {
    addForm.resetFields();
    setIsAddModalOpen(true);
  };

  // 提交新增
  const handleAddSubmit = async () => {
    try {
      const values = await addForm.validateFields();
      if (!values.reported_at) {
        // Fallback to now if not provided, though form will require it if it has a UI for it.
        // Wait, for account balance, the backend CreateBalanceManual just uses server time.
        // Wait, NO, we don't even submit reported_at because backend uses time.Now().In(shanghai) for Source="WEB".
        // Actually, backend sets ReportedAt: time.Now().In(shanghai), so value isn't needed.
      }

      const payload = { ...values };
      if (payload.snapshot_date) {
        payload.snapshot_date = payload.snapshot_date.format("YYYY-MM-DD");
      }
      if (payload.created_at) {
        payload.created_at = payload.created_at.toISOString();
      }
      if (payload.updated_at) {
        payload.updated_at = payload.updated_at.toISOString();
      }

      const response = await apiService.balances.createBalanceManual(payload);
      if (response.data?.success) {
        message.success("新增记录成功");
        setIsAddModalOpen(false);
        fetchData();
        fetchStats();
      }
    } catch (error) {
      if (error.errorFields) return; // Validation error
      message.error("新增失败: " + (error.response?.data?.error?.message || error.response?.data?.message || error.message));
    }
  };

  // 列定义
  const columns = [
    {
      title: "账号",
      dataIndex: "account",
      key: "account",
      width: 120,
      fixed: "left",
      render: (text) => (
        <span style={{ fontWeight: 500, color: "#fa8c16" }}>{text}</span>
      ),
    },
    {
      title: "交易所",
      dataIndex: "exchange",
      key: "exchange",
      width: 100,
      render: (text) => (
        <span style={{ fontWeight: 500, color: "#1890ff" }}>
          {formatExchangeName(text)}
        </span>
      ),
    },
    {
      title: "类型",
      dataIndex: "trade_type",
      key: "trade_type",
      width: 80,
      render: (tradeType) => (
        <Tag color={getTradeTypeColor(tradeType)}>
          {formatTradeType(tradeType)}
        </Tag>
      ),
    },
    {
      title: "资产",
      dataIndex: "asset",
      key: "asset",
      width: 80,
      render: (text) => (
        <span style={{ fontWeight: 600, color: "#722ed1" }}>{text}</span>
      ),
    },
    {
      title: "数量",
      dataIndex: "amount",
      key: "amount",
      width: 140,
      align: "right",
      render: (amount) => (
        <span style={{ fontFamily: "monospace", fontWeight: 500 }}>
          {Number(amount || 0).toFixed(8)}
        </span>
      ),
    },
    {
      title: "单价 (USDT)",
      dataIndex: "price",
      key: "price",
      width: 120,
      align: "right",
      render: (price) => (
        <span style={{ fontFamily: "monospace" }}>
          {price > 0 ? `$${formatVolume(price)}` : "-"}
        </span>
      ),
    },
    {
      title: "价值 (USDT)",
      dataIndex: "value_usdt",
      key: "value_usdt",
      width: 140,
      align: "right",
      render: (value) => (
        <span
          style={{ fontFamily: "monospace", fontWeight: 600, color: "#13c2c2" }}
        >
          ${formatVolume(value)}
        </span>
      ),
    },
    {
      title: "快照日期",
      dataIndex: "snapshot_date",
      key: "snapshot_date",
      width: 120,
      sorter: true,
      render: (date) => (
        <span style={{ fontFamily: "monospace", color: "#52c41a", fontWeight: 500 }}>{date}</span>
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="编辑">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定删除此记录？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ].filter(column => isAdmin || column.key !== 'action');

  // 处理筛选变化
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // 搜索（列表与统计卡片同步刷新）
  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchData({ page: 1 });
    fetchStats();
  };

  // 重置（含日期筛选与快捷时间 UI，避免界面仍显示「近7天」但实际未传 date 参数）
  const handleReset = () => {
    setTimePreset("");
    setShowCustomRange(false);
    setFilters(EMPTY_FILTERS);
    setPagination((prev) => ({ ...prev, current: 1 }));
    // 显式传入空筛选，避免 state 未更新导致请求仍带旧条件
    fetchData({ page: 1 }, EMPTY_FILTERS);
    fetchStats(EMPTY_FILTERS);
  };

  // 刷新
  const handleRefresh = () => {
    fetchData();
    fetchStats();
  };

  // 表格变化处理
  const handleTableChange = (paginationConfig, _, sorter) => {
    const params = {
      page: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    };

    if (sorter.field) {
      params.order_by = sorter.field;
      params.order_dir = sorter.order === "ascend" ? "asc" : "desc";
    }

    fetchData(params);
  };

  return (
    <ErrorBoundary>
      <div className="account-balance-container">
        {/* 统计卡片（跟随筛选条件联动） */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card stat-card-records">
              <div className="stat-header">
                <div className="stat-icon records">
                  <FileTextOutlined />
                </div>
                <div className="stat-label">总记录数</div>
              </div>
              <div className="stat-value" style={{ color: "#1890ff" }}>
                {(stats.total_records || 0).toLocaleString()}{" "}
                <span className="stat-unit">条</span>
              </div>
              <div className="stat-footer">
                资产种类 {stats.total_assets || 0} 种
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card stat-card-accounts">
              <div className="stat-header">
                <div className="stat-icon accounts">
                  <UserOutlined />
                </div>
                <div className="stat-label">账号数</div>
              </div>
              <div className="stat-value" style={{ color: "#722ed1" }}>
                {stats.total_accounts || 0} <span className="stat-unit">个</span>
              </div>
              <div className="stat-footer">
                覆盖交易所 {stats.total_exchanges || 0} 个
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card stat-card-total">
              <div className="stat-header">
                <div className="stat-icon total">
                  <WalletOutlined />
                </div>
                <div className="stat-label">最新快照总价值 (USDT)</div>
              </div>
              <div className="stat-value" style={{ color: "#13c2c2" }}>
                ${formatUSDT(stats.latest_value_usdt)}
              </div>
              <div className="stat-footer">
                {stats.latest_snapshot_date
                  ? `快照日期 ${stats.latest_snapshot_date}`
                  : "暂无快照数据"}
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card stat-card-split">
              <div className="stat-header">
                <div className="stat-icon pairs">
                  <SwapOutlined />
                </div>
                <div className="stat-label">现货 / 合约价值 (USDT)</div>
              </div>
              <div className="stat-split">
                <div className="stat-split-item">
                  <span className="stat-split-label spot">现货</span>
                  <span className="stat-split-value">
                    ${formatUSDT(stats.latest_spot_value_usdt)}
                  </span>
                </div>
                <div className="stat-split-item">
                  <span className="stat-split-label swap">合约</span>
                  <span className="stat-split-value">
                    ${formatUSDT(stats.latest_swap_value_usdt)}
                  </span>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        <Card title="账号资金管理">
          {/* 筛选工具栏 */}
          <div className="filter-toolbar">
            <Space wrap size="middle">
              <Select
                placeholder="选择账号"
                value={filters.account || undefined}
                onChange={(value) => handleFilterChange("account", value || "")}
                style={{ width: 150 }}
                allowClear
                showSearch
                loading={loadingAccounts}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {activeAccounts.map((item) => (
                  <Option key={`${item.account}-${item.exchange}`} value={item.account}>
                    {item.account} ({formatExchangeName(item.exchange)})
                  </Option>
                ))}
              </Select>

              <Select
                placeholder="选择交易所"
                value={filters.exchange || undefined}
                onChange={(value) => handleFilterChange("exchange", value || "")}
                style={{ width: 130 }}
                allowClear
                loading={loadingExchanges}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {exchanges.map((ex) => (
                  <Option key={ex.code || ex.name} value={ex.code || ex.name}>
                    {ex.name || ex.code}
                  </Option>
                ))}
              </Select>

              <Select
                placeholder="交易类型"
                value={filters.trade_type || undefined}
                onChange={(value) => handleFilterChange("trade_type", value || "")}
                style={{ width: 100 }}
                allowClear
              >
                <Option value="spot">现货</Option>
                <Option value="swap">合约</Option>
              </Select>

              <Select
                placeholder="业务线"
                value={filters.business_line_id}
                onChange={(value) => handleFilterChange("business_line_id", value)}
                style={{ width: 130 }}
                allowClear
                showSearch
                loading={loadingMasterData}
                optionFilterProp="children"
              >
                <Option value={0}>未分配</Option>
                {businessLines.map((bl) => (
                  <Option key={bl.id} value={bl.id}>
                    {bl.name}
                  </Option>
                ))}
              </Select>

              <Select
                placeholder="策略类型"
                value={filters.strategy_type_id}
                onChange={(value) => handleFilterChange("strategy_type_id", value)}
                style={{ width: 130 }}
                allowClear
                showSearch
                loading={loadingMasterData}
                optionFilterProp="children"
              >
                <Option value={0}>未分配</Option>
                {strategyTypes.map((st) => (
                  <Option key={st.id} value={st.id}>
                    {st.name}
                  </Option>
                ))}
              </Select>

              <Input
                placeholder="资产名称"
                value={filters.asset}
                onChange={(e) => handleFilterChange("asset", e.target.value)}
                style={{ width: 100 }}
                allowClear
                onPressEnter={handleSearch}
              />

              <Select
                placeholder="时间筛选"
                value={timePreset || undefined}
                onChange={(value) => {
                  setTimePreset(value);
                  if (value === "custom") {
                    setShowCustomRange(true);
                  } else {
                    setShowCustomRange(false);
                    const range = getDateRangeFromPreset(value);
                    handleFilterChange("dateRange", range);
                  }
                }}
                style={{ width: 130 }}
                allowClear
                onClear={() => {
                  setTimePreset("");
                  setShowCustomRange(false);
                  handleFilterChange("dateRange", null);
                }}
              >
                {timePresets.map((p) => (
                  <Option key={p.value} value={p.value}>
                    {p.label}
                  </Option>
                ))}
              </Select>
              {showCustomRange && (
                <RangePicker
                  value={filters.dateRange}
                  onChange={(value) => handleFilterChange("dateRange", value)}
                  format="YYYY-MM-DD"
                  placeholder={["开始日期", "结束日期"]}
                  style={{ minWidth: 240 }}
                />
              )}

              <Button type="primary" onClick={handleSearch} icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
              {isAdmin && (
                <Button
                  onClick={handleRecalculate}
                  icon={<ReloadOutlined />}
                  danger
                >
                  重算近30天权益
                </Button>
              )}
              <Button onClick={handleRefresh} icon={<ReloadOutlined />}>
                刷新
              </Button>
              {isAdmin && (
                <Button type="primary" onClick={handleOpenAdd}>
                  新增记录
                </Button>
              )}
              {isAdmin && (
                <Popconfirm
                  title={`确定要删除选中的 ${selectedRowKeys.length} 条记录吗？`}
                  description="此操作不可恢复。"
                  onConfirm={handleBatchDelete}
                  okText="确定"
                  cancelText="取消"
                  disabled={selectedRowKeys.length === 0}
                >
                  <Button
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={selectedRowKeys.length === 0}
                  >
                    批量删除
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </div>

          {/* 数据表格 */}
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            loading={loading}
            onChange={handleTableChange}
            rowSelection={isAdmin ? {
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
            } : null}
            rowKey="id"
            scroll={{ x: "max-content" }}
            size="small"
            bordered
          />
        </Card>

        {/* 新增记录弹窗 */}
        <Modal
          title="新增资金记录"
          open={isAddModalOpen}
          onOk={handleAddSubmit}
          onCancel={() => setIsAddModalOpen(false)}
          destroyOnHidden
        >
          <Form form={addForm} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="account" label="账号标识" rules={[{ required: true, message: "请输入账号" }]}>
                  <Input placeholder="例如: user@example.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="exchange" label="交易所" rules={[{ required: true, message: "请选择交易所" }]}>
                  <Select placeholder="选择交易所" showSearch loading={loadingExchanges}>
                    {exchanges.map((ex) => (
                      <Option key={ex.code || ex.id} value={ex.code}>
                        {ex.name || ex.code}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="trade_type" label="交易类型" rules={[{ required: true, message: "请选择交易类型" }]}>
                  <Select placeholder="选择类型">
                    <Option value="spot">现货</Option>
                    <Option value="swap">合约</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="asset" label="资产名称" rules={[{ required: true, message: "请输入资产名称" }]}>
                  <Input placeholder="例如: USDT" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="amount" label="数量" rules={[{ required: true, message: "请输入数量" }]}>
                  <Input type="number" step="0.00000001" placeholder="设定数量" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="price" label="价格">
                  <Input type="number" step="0.00000001" placeholder="设定价格" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="snapshot_date"
                  label="快照日期"
                  extra="留空则默认为今天"
                >
                  <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>

        {/* 编辑资金记录弹窗 */}
        <Modal
          title="编辑资金记录"
          open={isEditModalOpen}
          onOk={() => form.submit()}
          onCancel={() => setIsEditModalOpen(false)}
          destroyOnHidden
        >
          <Form form={form} layout="vertical" onFinish={handleEditSubmit}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="account" label="账号标识" rules={[{ required: true, message: "请输入账号" }]}>
                  <Input placeholder="例如: user@example.com" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="exchange" label="交易所" rules={[{ required: true, message: "请选择交易所" }]}>
                  <Select placeholder="选择交易所" showSearch loading={loadingExchanges}>
                    {exchanges.map((ex) => (
                      <Option key={ex.code || ex.id} value={ex.code}>
                        {ex.name || ex.code}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="trade_type" label="交易类型" rules={[{ required: true, message: "请选择交易类型" }]}>
                  <Select placeholder="选择类型">
                    <Option value="spot">现货</Option>
                    <Option value="swap">合约</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="asset" label="资产名称" rules={[{ required: true, message: "请输入资产名称" }]}>
                  <Input placeholder="例如: USDT" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="amount" label="数量" rules={[{ required: true, message: "请输入数量" }]}>
                  <Input type="number" step="0.00000001" placeholder="设定数量" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="price" label="价格">
                  <Input type="number" step="0.00000001" placeholder="设定价格" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="snapshot_date" label="快照日期" rules={[{ required: true, message: "请选择快照日期" }]}>
                  <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </ErrorBoundary>
  );
};

export default AccountBalanceTable;
